import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Card,
  CardContent,
  ButtonGroup
} from '@mui/material';
import {
  ShowChart as LineIcon,
  GetApp as ExportIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { useAuth } from '../../../../auth/useAuth';
import { useHedgeFundData } from '../../contexts/HedgeFundDataContext';
import MultiChartExportDialog from './MultiChartExportDialog';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface MonitorDataRow {
  MonitorDataID: number;
  BackstopID: number;
  FieldID: number;
  MonitorDate: string;
  FieldValue: number;
  Note?: string;
  FundName: string;
  FieldName: string;
  SoftLower?: number;
  HardLower?: number;
  SoftUpper?: number;
  HardUpper?: number;
  SoftViolation: boolean;
  HardViolation: boolean;
  RiskIndicatorName?: string;
  RiskLevel?: number;
}

interface MonitorField {
  FieldID: number;
  FieldName: string;
  RiskIndicatorID: number;
}

const MonitorDataReport: React.FC = () => {
  const { getAccessToken, user } = useAuth();
  const { funds } = useHedgeFundData();
  const chartRef = useRef<any>(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [selectedFund, setSelectedFund] = useState<string>('');
  const [selectedField, setSelectedField] = useState<string>('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [showThresholds, setShowThresholds] = useState<boolean>(true);

  const [monitorFields, setMonitorFields] = useState<MonitorField[]>([]);
  const [monitorData, setMonitorData] = useState<MonitorDataRow[]>([]);
  const [chartData, setChartData] = useState<any>(null);
  const [multiExportOpen, setMultiExportOpen] = useState(false);

  // Load user preference for showing thresholds
  useEffect(() => {
    const userEmail = (user as any)?.email || (user as any)?.preferred_username || user?.username || 'default';
    const savedPreference = localStorage.getItem(`monitorDataReport_showThresholds_${userEmail}`);
    if (savedPreference !== null) {
      setShowThresholds(savedPreference === 'true');
    }
  }, [user]);

  // Save user preference when checkbox changes
  useEffect(() => {
    const userEmail = (user as any)?.email || (user as any)?.preferred_username || user?.username || 'default';
    localStorage.setItem(`monitorDataReport_showThresholds_${userEmail}`, String(showThresholds));
  }, [showThresholds, user]);

  // Load fields when fund changes
  useEffect(() => {
    if (selectedFund) {
      loadMonitorFields(selectedFund);
      setSelectedField(''); // Reset field selection
      setMonitorData([]); // Clear data
      setChartData(null); // Clear chart
      setStartDate(null);
      setEndDate(new Date());
    } else {
      setMonitorFields([]);
      setSelectedField('');
    }
  }, [selectedFund]);

  // Load date range when field is selected
  useEffect(() => {
    if (selectedFund && selectedField) {
      loadDateRange();
    }
  }, [selectedFund, selectedField]);

  // Update chart when data or showThresholds changes
  useEffect(() => {
    if (monitorData.length > 0 && selectedFund && selectedField) {
      updateChart();
    }
  }, [monitorData, showThresholds, startDate, endDate]);

  const loadDateRange = async () => {
    try {
      const token = await getAccessToken();
      if (!token) return;

      const params = new URLSearchParams();
      params.append('backstop_id', selectedFund);
      params.append('field_id', selectedField);

      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}/api/hedgefund/monitor-data?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data: MonitorDataRow[] = await response.json();
        
        if (data.length > 0) {
          // Sort by date to find first and last
          data.sort((a, b) => new Date(a.MonitorDate).getTime() - new Date(b.MonitorDate).getTime());
          
          // Set date range based on actual data
          const firstDate = new Date(data[0].MonitorDate);
          const lastDate = new Date(data[data.length - 1].MonitorDate);
          
          setStartDate(firstDate);
          setEndDate(lastDate);
        }
      }
    } catch (err) {
      console.error('Failed to load date range:', err);
    }
  };

  const loadMonitorFields = async (fundId: string) => {
    try {
      setLoading(true);
      const token = await getAccessToken();
      if (!token) {
        setError('No access token available');
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}/api/hedgefund/monitor-fields-for-fund/${fundId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setMonitorFields(data);
      } else {
        setError('Failed to load monitor fields');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load monitor fields');
    } finally {
      setLoading(false);
    }
  };

  const loadMonitorData = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await getAccessToken();
      if (!token) {
        setError('No access token available');
        return;
      }

      const params = new URLSearchParams();
      params.append('backstop_id', selectedFund);
      params.append('field_id', selectedField);

      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}/api/hedgefund/monitor-data?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data: MonitorDataRow[] = await response.json();
        
        if (data.length > 0) {
          // Sort by date
          data.sort((a, b) => new Date(a.MonitorDate).getTime() - new Date(b.MonitorDate).getTime());
          
          setMonitorData(data);
          // Don't reset dates here - respect user's selections
        } else {
          setMonitorData([]);
          setError('No data available for this fund and field combination');
        }
      } else {
        setError('Failed to load monitor data');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load monitor data');
    } finally {
      setLoading(false);
    }
  };

  const updateChart = () => {
    if (monitorData.length === 0 || !startDate || !endDate) {
      setChartData(null);
      return;
    }

    // Filter data by date range
    const filteredData = monitorData.filter(row => {
      const rowDate = new Date(row.MonitorDate);
      if (startDate && rowDate < startDate) return false;
      if (endDate && rowDate > endDate) return false;
      return true;
    });

    if (filteredData.length === 0) {
      setError('No data in selected date range');
      setChartData(null);
      return;
    }

    // Format dates for display
    const formatDate = (date: Date) => {
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${months[date.getMonth()]}-${date.getFullYear().toString().slice(-2)}`;
    };

    // Generate all month labels from start to end date
    const generateMonthLabels = () => {
      const labels: string[] = [];
      const current = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
      const end = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
      
      while (current <= end) {
        labels.push(formatDate(current));
        current.setMonth(current.getMonth() + 1);
      }
      return labels;
    };

    const allLabels = generateMonthLabels();
    
    // Create a map of data by month-year
    const dataMap = new Map<string, MonitorDataRow[]>();
    filteredData.forEach(row => {
      const date = new Date(row.MonitorDate);
      const key = formatDate(date);
      if (!dataMap.has(key)) {
        dataMap.set(key, []);
      }
      dataMap.get(key)!.push(row);
    });

    // Map values to labels (take last value in each month if multiple entries)
    const values = allLabels.map(label => {
      const monthData = dataMap.get(label);
      return monthData && monthData.length > 0 
        ? monthData[monthData.length - 1].FieldValue 
        : null;
    });

    const datasets: any[] = [
      {
        label: filteredData[0]?.FieldName || 'Field Value',
        data: values,
        borderColor: '#1976d2',
        backgroundColor: 'rgba(25, 118, 210, 0.1)',
        fill: true,
        tension: 0.4,
        pointRadius: 4,
        pointHoverRadius: 6,
        borderWidth: 2
      }
    ];

    // Add threshold lines if checkbox is enabled
    if (showThresholds && filteredData.length > 0) {
      // Map threshold values with forward-filling (carry last known value forward)
      let lastSoftLower: number | null = null;
      let lastHardLower: number | null = null;
      let lastSoftUpper: number | null = null;
      let lastHardUpper: number | null = null;
      
      const softLowerValues = allLabels.map(label => {
        const monthData = dataMap.get(label);
        if (monthData && monthData.length > 0) {
          const value = monthData[monthData.length - 1].SoftLower;
          if (value !== null && value !== undefined) {
            lastSoftLower = value;
          }
        }
        // Always return last known value (forward-fill)
        return lastSoftLower;
      });
      
      const hardLowerValues = allLabels.map(label => {
        const monthData = dataMap.get(label);
        if (monthData && monthData.length > 0) {
          const value = monthData[monthData.length - 1].HardLower;
          if (value !== null && value !== undefined) {
            lastHardLower = value;
          }
        }
        return lastHardLower;
      });
      
      const softUpperValues = allLabels.map(label => {
        const monthData = dataMap.get(label);
        if (monthData && monthData.length > 0) {
          const value = monthData[monthData.length - 1].SoftUpper;
          if (value !== null && value !== undefined) {
            lastSoftUpper = value;
          }
        }
        return lastSoftUpper;
      });
      
      const hardUpperValues = allLabels.map(label => {
        const monthData = dataMap.get(label);
        if (monthData && monthData.length > 0) {
          const value = monthData[monthData.length - 1].HardUpper;
          if (value !== null && value !== undefined) {
            lastHardUpper = value;
          }
        }
        return lastHardUpper;
      });
      
      // Check if any threshold values exist
      const hasSoftLower = softLowerValues.some(val => val !== null && val !== undefined);
      const hasHardLower = hardLowerValues.some(val => val !== null && val !== undefined);
      const hasSoftUpper = softUpperValues.some(val => val !== null && val !== undefined);
      const hasHardUpper = hardUpperValues.some(val => val !== null && val !== undefined);
      
      if (hasSoftLower) {
        datasets.push({
          label: 'Soft Lower (L1)',
          data: softLowerValues,
          borderColor: '#ff9800',
          borderDash: [5, 5],
          borderWidth: 2,
          pointRadius: 0,
          fill: false,
          tension: 0,
          spanGaps: false
        });
      }

      if (hasHardLower) {
        datasets.push({
          label: 'Hard Lower (L2)',
          data: hardLowerValues,
          borderColor: '#f44336',
          borderDash: [10, 5],
          borderWidth: 2,
          pointRadius: 0,
          fill: false,
          tension: 0,
          spanGaps: false
        });
      }

      if (hasSoftUpper) {
        datasets.push({
          label: 'Soft Upper (U1)',
          data: softUpperValues,
          borderColor: '#ff9800',
          borderDash: [5, 5],
          borderWidth: 2,
          pointRadius: 0,
          fill: false,
          tension: 0,
          spanGaps: false
        });
      }

      if (hasHardUpper) {
        datasets.push({
          label: 'Hard Upper (U2)',
          data: hardUpperValues,
          borderColor: '#f44336',
          borderDash: [10, 5],
          borderWidth: 2,
          pointRadius: 0,
          fill: false,
          tension: 0,
          spanGaps: false
        });
      }
    }

    setChartData({
      labels: allLabels,
      datasets
    });
  };

  const handleExportExcel = () => {
    try {
      if (!monitorData.length) {
        setError('No data to export');
        return;
      }

      // Filter data by date range
      const filteredData = monitorData.filter(row => {
        const rowDate = new Date(row.MonitorDate);
        if (startDate && rowDate < startDate) return false;
        if (endDate && rowDate > endDate) return false;
        return true;
      });

      const fundName = funds.find(f => f.BackstopID.toString() === selectedFund)?.FundName || 'Unknown';
      const fieldName = monitorFields.find(f => f.FieldID.toString() === selectedField)?.FieldName || 'Unknown';

      // Create CSV content (similar to BrowseEditView)
      let csvContent = 'Monitor Data Report\n';
      csvContent += `Export Date: ${new Date().toLocaleString()}\n`;
      csvContent += `Total Records: ${filteredData.length}\n\n`;
      
      csvContent += 'Selection Criteria:\n';
      csvContent += `Fund: ${fundName}\n`;
      csvContent += `Field: ${fieldName}\n`;
      csvContent += `Start Date: ${startDate ? startDate.toLocaleDateString() : 'All time'}\n`;
      csvContent += `End Date: ${endDate ? endDate.toLocaleDateString() : 'Current'}\n\n`;
      
      csvContent += 'Date,Value,L1,L2,U1,U2,Soft Violation,Hard Violation,Note\n';
      
      filteredData.forEach(row => {
        const date = new Date(row.MonitorDate).toLocaleDateString();
        const value = row.FieldValue.toFixed(4);
        const l1 = row.SoftLower != null ? row.SoftLower.toFixed(4) : '';
        const l2 = row.HardLower != null ? row.HardLower.toFixed(4) : '';
        const u1 = row.SoftUpper != null ? row.SoftUpper.toFixed(4) : '';
        const u2 = row.HardUpper != null ? row.HardUpper.toFixed(4) : '';
        const soft = row.SoftViolation ? 'Yes' : 'No';
        const hard = row.HardViolation ? 'Yes' : 'No';
        const note = (row.Note || '').replace(/,/g, ';').replace(/"/g, '""');
        
        csvContent += `"${date}",${value},${l1},${l2},${u1},${u2},"${soft}","${hard}","${note}"\n`;
      });

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `monitor_data_${fundName.replace(/\s+/g, '_')}_${fieldName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setSuccess('Data exported to CSV successfully');
    } catch (err) {
      setError(`CSV export failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const handleExportPDF = () => {
    try {
      if (!monitorData.length || !chartData) {
        setError('No data to export');
        return;
      }

      // Filter data by date range
      const filteredData = monitorData.filter(row => {
        const rowDate = new Date(row.MonitorDate);
        if (startDate && rowDate < startDate) return false;
        if (endDate && rowDate > endDate) return false;
        return true;
      });

      const fundName = funds.find(f => f.BackstopID.toString() === selectedFund)?.FundName || 'Unknown';
      const fieldName = monitorFields.find(f => f.FieldID.toString() === selectedField)?.FieldName || 'Unknown';

      const doc = new jsPDF('landscape', 'pt', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      let yPos = 40;

      // Title
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('Monitor Data Report', pageWidth / 2, yPos, { align: 'center' });
      yPos += 30;

      // Selection Criteria
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Selection Criteria:', 40, yPos);
      yPos += 15;

      doc.setFont('helvetica', 'normal');
      doc.text(`Fund: ${fundName}`, 50, yPos);
      yPos += 12;
      doc.text(`Field: ${fieldName}`, 50, yPos);
      yPos += 12;
      doc.text(`Date Range: ${startDate ? startDate.toLocaleDateString() : 'All time'} - ${endDate ? endDate.toLocaleDateString() : 'Current'}`, 50, yPos);
      yPos += 12;
      doc.text(`Total Records: ${filteredData.length}`, 50, yPos);
      yPos += 12;
      doc.text(`Export Date: ${new Date().toLocaleString()}`, 50, yPos);
      yPos += 20;

      // Chart Image
      if (chartRef.current) {
        const chartImage = chartRef.current.toBase64Image();
        const imgWidth = pageWidth - 80;
        const imgHeight = 200;
        doc.addImage(chartImage, 'PNG', 40, yPos, imgWidth, imgHeight);
        yPos += imgHeight + 20;
      }

      // Data Table
      const tableData = filteredData.map(row => [
        new Date(row.MonitorDate).toLocaleDateString(),
        row.FieldValue.toFixed(2),
        row.SoftLower != null ? row.SoftLower.toFixed(2) : '-',
        row.HardLower != null ? row.HardLower.toFixed(2) : '-',
        row.SoftUpper != null ? row.SoftUpper.toFixed(2) : '-',
        row.HardUpper != null ? row.HardUpper.toFixed(2) : '-',
        row.SoftViolation ? 'Yes' : '',
        row.HardViolation ? 'Yes' : ''
      ]);

      autoTable(doc, {
        head: [['Date', 'Value', 'L1', 'L2', 'U1', 'U2', 'Soft', 'Hard']],
        body: tableData,
        startY: yPos,
        styles: {
          fontSize: 8,
          cellPadding: 3
        },
        headStyles: {
          fillColor: [26, 35, 126],
          textColor: 255,
          fontStyle: 'bold'
        },
        columnStyles: {
          0: { cellWidth: 80 },
          1: { cellWidth: 60, halign: 'right', fontStyle: 'bold' },
          2: { cellWidth: 50, halign: 'right' },
          3: { cellWidth: 50, halign: 'right' },
          4: { cellWidth: 50, halign: 'right' },
          5: { cellWidth: 50, halign: 'right' },
          6: { cellWidth: 40 },
          7: { cellWidth: 40 }
        },
        margin: { left: 40, right: 40 }
      });

      // Save PDF
      const fileName = `monitor_data_${fundName.replace(/\s+/g, '_')}_${fieldName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);

      setSuccess('Data exported to PDF successfully');
    } catch (err) {
      setError(`PDF export failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          usePointStyle: true,
          padding: 15
        }
      },
      title: {
        display: true,
        text: monitorData[0] ? `${monitorData[0].FundName} - ${monitorData[0].FieldName}` : 'Monitor Data',
        font: {
          size: 16,
          weight: 'bold' as const
        }
      },
      tooltip: {
        callbacks: {
          label: function(context: any) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += context.parsed.y.toFixed(4);
            }
            return label;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: false,
        title: {
          display: true,
          text: 'Field Value'
        }
      },
      x: {
        type: 'category' as const,
        title: {
          display: true,
          text: 'Date'
        },
        ticks: {
          autoSkip: true,
          maxTicksLimit: 12,
          maxRotation: 45,
          minRotation: 0
        }
      }
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        {/* Header */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LineIcon color="primary" />
                Monitor Data Report
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Chart specific risk parameters over time for a chosen fund with export capabilities
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<PdfIcon />}
              onClick={() => setMultiExportOpen(true)}
              size="small"
            >
              Export Multiple Charts
            </Button>
          </Box>
        </Paper>

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Selection Controls */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Report Parameters
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Select Fund</InputLabel>
                <Select
                  value={selectedFund}
                  onChange={(e) => setSelectedFund(e.target.value)}
                  label="Select Fund"
                >
                  <MenuItem value="">
                    <em>Select a fund...</em>
                  </MenuItem>
                  {funds.map((fund) => (
                    <MenuItem key={fund.BackstopID} value={fund.BackstopID.toString()}>
                      {fund.FundName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small" disabled={!selectedFund}>
                <InputLabel>Select Field</InputLabel>
                <Select
                  value={selectedField}
                  onChange={(e) => setSelectedField(e.target.value)}
                  label="Select Field"
                >
                  <MenuItem value="">
                    <em>Select a field...</em>
                  </MenuItem>
                  {monitorFields.map((field) => (
                    <MenuItem key={field.FieldID} value={field.FieldID.toString()}>
                      {field.FieldName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(newValue) => {
                  setStartDate(newValue);
                  if (monitorData.length > 0) updateChart();
                }}
                disabled={!startDate}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(newValue) => {
                  setEndDate(newValue);
                  if (monitorData.length > 0) updateChart();
                }}
                disabled={!endDate}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showThresholds}
                      onChange={(e) => setShowThresholds(e.target.checked)}
                      disabled={!startDate || !endDate}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">Show Thresholds</Typography>}
                />
              </Box>
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                <Button
                  variant="contained"
                  size="medium"
                  onClick={() => loadMonitorData()}
                  disabled={!selectedFund || !selectedField || loading}
                  startIcon={<LineIcon />}
                >
                  {loading ? 'Loading...' : 'Show Report'}
                </Button>
              </Box>
            </Grid>
          </Grid>

          {/* Export Buttons */}
          {chartData && (
            <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <ButtonGroup variant="outlined" size="small">
                <Button startIcon={<ExcelIcon />} onClick={handleExportExcel}>
                  Export Excel
                </Button>
                <Button startIcon={<PdfIcon />} onClick={handleExportPDF}>
                  Export PDF
                </Button>
              </ButtonGroup>
            </Box>
          )}
        </Paper>

        {/* Chart Display */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && chartData && (
          <Paper sx={{ p: 2 }}>
            <Box sx={{ height: 400 }}>
              <Line ref={chartRef} data={chartData} options={chartOptions} />
            </Box>
            
            {/* Data Summary */}
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">
                        Total Data Points
                      </Typography>
                      <Typography variant="h5">
                        {monitorData.filter(row => {
                          const rowDate = new Date(row.MonitorDate);
                          if (startDate && rowDate < startDate) return false;
                          if (endDate && rowDate > endDate) return false;
                          return true;
                        }).length}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">
                        Date Range
                      </Typography>
                      <Typography variant="body1">
                        {startDate?.toLocaleDateString()} - {endDate?.toLocaleDateString()}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">
                        Soft Violations
                      </Typography>
                      <Typography variant="h5" color="warning.main">
                        {monitorData.filter(row => {
                          const rowDate = new Date(row.MonitorDate);
                          if (startDate && rowDate < startDate) return false;
                          if (endDate && rowDate > endDate) return false;
                          return row.SoftViolation;
                        }).length}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">
                        Hard Violations
                      </Typography>
                      <Typography variant="h5" color="error.main">
                        {monitorData.filter(row => {
                          const rowDate = new Date(row.MonitorDate);
                          if (startDate && rowDate < startDate) return false;
                          if (endDate && rowDate > endDate) return false;
                          return row.HardViolation;
                        }).length}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        )}

        {!loading && !chartData && !error && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <LineIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Chart Data Available
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Select a fund and field to generate the monitor data chart
            </Typography>
          </Paper>
        )}
        <MultiChartExportDialog
          open={multiExportOpen}
          onClose={() => setMultiExportOpen(false)}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default MonitorDataReport;
